import { ProviderCategory } from '@/api/model/providerCategory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowUpDown, Search } from 'lucide-react';
import React from 'react';

export type VoucherSortOrder = 'newest' | 'oldest';

type VoucherFilterControlsProps = {
  selectedCategory: string;
  searchQuery: string;
  onCategoryChange: (category: string) => void;
  onSearchChange: (query: string) => void;
  sortOrder?: VoucherSortOrder;
  onSortChange?: (nextOrder: VoucherSortOrder) => void;
};

export const VoucherFilterControls: React.FC<VoucherFilterControlsProps> = ({
  selectedCategory,
  searchQuery,
  onCategoryChange,
  onSearchChange,
  sortOrder = 'newest',
  onSortChange,
}) => {
  const [searchInput, setSearchInput] = React.useState(searchQuery);

  const handleSortToggle = React.useCallback(() => {
    if (!onSortChange) {
      return;
    }

    const nextOrder = sortOrder === 'newest' ? 'oldest' : 'newest';
    onSortChange(nextOrder);
  }, [onSortChange, sortOrder]);

  const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
  }, []);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      onSearchChange(searchInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput, onSearchChange]);

  React.useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  return (
    <div className='flex w-full flex-wrap items-center justify-between gap-4'>
      {/* Tabs and Sort */}
      <div className='rounded-lg flex flex-nowrap justify-between w-full items-center gap-3 md:gap-4 lg:gap-6'>
        {/* Mobile: dropdown select */}
        <div className='md:hidden flex-shrink-0'>
          <Select value={selectedCategory} onValueChange={onCategoryChange}>
            <SelectTrigger>
              <SelectValue placeholder='Kategorie' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Alle</SelectItem>
              <SelectItem value={ProviderCategory.GASTRONOMIE}>Gastronomie</SelectItem>
              <SelectItem value={ProviderCategory.ERLEBEN}>Erleben</SelectItem>
              <SelectItem value={ProviderCategory.SHOPPING}>Shopping</SelectItem>
              <SelectItem value={ProviderCategory.SERVICES}>Services</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: tabs */}
        <div className='hidden md:block min-w-0'>
          <Tabs value={selectedCategory} onValueChange={onCategoryChange}>
            <TabsList className='flex-wrap'>
              <TabsTrigger value='alle'>Alle</TabsTrigger>
              <TabsTrigger value={ProviderCategory.GASTRONOMIE}>Gastronomie</TabsTrigger>
              <TabsTrigger value={ProviderCategory.ERLEBEN}>Erleben</TabsTrigger>
              <TabsTrigger value={ProviderCategory.SHOPPING}>Shopping</TabsTrigger>
              <TabsTrigger value={ProviderCategory.SERVICES}>Services</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className='flex items-center gap-2 md:gap-3 lg:gap-5 flex-shrink-0'>
          <Button
            variant='secondary'
            className='h-[38px] rounded-2xl px-3 md:px-4 lg:px-[17px] text-sm md:text-[14px] tracking-[0.56px] text-muted-foreground flex items-center gap-1 md:gap-2 lg:gap-[7px] whitespace-nowrap'
            onClick={handleSortToggle}
            aria-pressed={sortOrder === 'oldest'}
          >
            <ArrowUpDown className='size-4 text-muted-foreground flex-shrink-0' aria-hidden='true' />
            <span className='hidden lg:inline'>Sortieren nach:</span>
            <span>{sortOrder === 'newest' ? 'Neueste' : 'Älteste'}</span>
          </Button>
          <div className='relative flex-shrink-0'>
            <Input
              placeholder='Gutschein suchen...'
              className='h-[38px] rounded-lg pr-9 w-[140px] md:w-[160px] lg:w-auto text-sm md:text-[14px] tracking-[0.56px] placeholder:text-zinc-400'
              value={searchInput}
              onChange={handleSearchChange}
            />
            <Search
              size={16}
              className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400'
              aria-hidden='true'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherFilterControls;
